import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getApiBase } from '../apiBase';
import { authFetch, canEditDetails, getUsername, isAdmin, isCollector, isManagerOrAdmin } from '../auth';
import PaymentReceiptPdfButton from './PaymentReceiptPdfButton';
import {
  LoadingSpinner,
  TableFiltersBar,
  TablePaginationBar,
  filterControl,
  filterLabel,
  filterLabelNarrow,
  inDateRange,
  mobileCardList,
  MobileRowCard,
  rowMatchesQuery,
  scrollTableWrap,
  stickyFirstTd,
  stickyFirstTh,
  stickyThead,
  useTablePagination,
  modalPanelClass,
  ModalBackdrop,
} from './tableToolbar';
import RowDetailModal, { detailRowAttrs } from './RowDetailModal';
import RecordPaymentModal from './RecordPaymentModal';
import CollectorSeparateBillSettlementModal from './CollectorSeparateBillSettlementModal';
import { useSeparateBillSettlementFlow } from './useShopCollectorSettings';
import { getPaymentCheques, getPaymentCdmDeposits, getPaymentOnlineTransfers } from './paymentCheques';
import { usePrinter } from '../printer/PrinterProvider';
import { todayYmdLocal, useCollectorCollectionClosed } from './collectionDayClose';
import { COLLECTOR_CASH_PRINT_TODAY_ONLY, collectorMayPrintCashCollection } from './paymentReceipt';

const apiBase = getApiBase();

function money(n) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'LKR',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

export default function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedCustomerPrefill = useRef(false);
  const { requestAutoPrint, requestPrint } = usePrinter();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [modalCustomerId, setModalCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [detailPayment, setDetailPayment] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [separateBillModalOpen, setSeparateBillModalOpen] = useState(false);
  const { useSeparateBillSettlement, loading: collectorSettingsLoading } = useSeparateBillSettlementFlow();
  const today = useMemo(() => todayYmdLocal(), []);
  const { closed: collectionClosed, loading: collectionClosedLoading } = useCollectorCollectionClosed(today);
  const paymentsLocked = isCollector() && collectionClosed;

  const loadCustomers = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/customers`);
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setCustomers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiBase}/api/payments`);
      if (!res.ok) throw new Error('Failed to load payments');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Could not load data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (appliedCustomerPrefill.current || collectorSettingsLoading || collectionClosedLoading) return;
    const customerId = searchParams.get('customerId')?.trim();
    if (!customerId) return;
    appliedCustomerPrefill.current = true;
    setCustomerFilter(customerId);
    if (searchParams.get('record') === '1' && !paymentsLocked) {
      setEditPayment(null);
      setModalCustomerId(customerId);
      if (useSeparateBillSettlement) {
        setSeparateBillModalOpen(true);
      } else {
        setModalOpen(true);
      }
    }
    setSearchParams({}, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    useSeparateBillSettlement,
    collectorSettingsLoading,
    collectionClosedLoading,
    paymentsLocked,
  ]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (!inDateRange(r.date, dateFrom, dateTo)) return false;
      if (customerFilter && r.customerId !== customerFilter) return false;
      if (
        !rowMatchesQuery(search, [
          r.date,
          r.billNumber,
          r.customerName,
          r.customerId,
          r.note,
          r.recordedBy,
          r.cancelled ? 'cancelled' : '',
          String(r.amount),
          ...getPaymentCheques(r).flatMap((c) => [c.chequeDate, c.chequeNumber]),
          ...getPaymentCdmDeposits(r).flatMap((d) => [d.cdmNumber, d.cdmDate, d.bankAccountId]),
          ...getPaymentOnlineTransfers(r).flatMap((t) => [t.reference, t.transferDate, t.bankAccountId]),
        ])
      ) {
        return false;
      }
      return true;
    });
  }, [rows, search, dateFrom, dateTo, customerFilter]);

  const pagination = useTablePagination(filteredRows.length, [
    search,
    dateFrom,
    dateTo,
    customerFilter,
  ]);
  const pagedRows = useMemo(
    () => filteredRows.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredRows, pagination.offset, pagination.pageSize]
  );

  const openModal = (prefillCustomerId = '') => {
    if (paymentsLocked) return;
    setEditPayment(null);
    setModalCustomerId(prefillCustomerId || '');
    if (useSeparateBillSettlement) {
      setSeparateBillModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  const closeSeparateBillModal = () => {
    setSeparateBillModalOpen(false);
    setModalCustomerId('');
  };

  const openPaymentEdit = (payment) => {
    if (!payment?.id || payment.cancelled) return;
    setEditPayment(payment);
    setModalCustomerId(payment.customerId || '');
    setDetailPayment(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditPayment(null);
    setModalCustomerId('');
  };

  const openCancelConfirm = (payment) => {
    if (!isAdmin() || !payment?.id || payment.cancelled) return;
    setCancelError(null);
    setCancelTarget(payment);
  };

  const closeCancelConfirm = () => {
    if (cancelBusy) return;
    setCancelTarget(null);
    setCancelError(null);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget?.id) return;
    const username = getUsername();
    if (!username) {
      setCancelError('Sign in as admin to cancel payment receipts.');
      return;
    }
    setCancelBusy(true);
    setCancelError(null);
    try {
      const res = await authFetch(`${apiBase}/api/payments/${encodeURIComponent(cancelTarget.id)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cancelledBy: username }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCancelError(data.error || 'Could not cancel payment receipt');
        return;
      }
      if (detailPayment?.id === cancelTarget.id) {
        setDetailPayment({ ...detailPayment, ...data, cancelled: true });
      }
      setCancelTarget(null);
      await load();
    } catch {
      setCancelError('Could not reach the server.');
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          {useSeparateBillSettlement
            ? 'Record cash or cheque payments, then allocate across pending invoices.'
            : 'Record cash and cheque payments against customer balances.'}
        </p>
        <button
          type="button"
          onClick={() => openModal()}
          disabled={collectorSettingsLoading || paymentsLocked}
          title={paymentsLocked ? 'Collection for today is over. You cannot add more payments.' : undefined}
          className="inline-flex w-full shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:brightness-[1.03] disabled:opacity-60 sm:w-auto"
        >
          Record payment
        </button>
      </div>

      {paymentsLocked ? (
        <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-950 ring-1 ring-amber-100" role="status">
          Collection for today is over. You cannot add more payments.
        </p>
      ) : null}

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-100" role="alert">
          {error}
        </p>
      ) : null}

      <TableFiltersBar
        hint={
          !loading && rows.length > 0
            ? `Showing ${filteredRows.length} of ${rows.length} payment${rows.length === 1 ? '' : 's'}`
            : null
        }
      >
        <label className={filterLabel}>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer, bill #, note, amount…"
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          From date
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          To date
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={filterControl}
          />
        </label>
        <label className={filterLabel}>
          Customer
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className={filterControl}
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </TableFiltersBar>

      <div className="space-y-3">
      <div className={mobileCardList}>
        {loading ? (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
            <LoadingSpinner />
          </p>
        ) : rows.length === 0 ? (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
            No payments yet. Record one to update customer balances.
          </p>
        ) : filteredRows.length === 0 ? (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
            No payments match your search or filters.
          </p>
        ) : (
          pagedRows.map((r) => (
            <MobileRowCard
              key={r.id}
              title={r.customerName || '—'}
              className={r.cancelled ? 'opacity-75' : ''}
              subtitle={
                <span className="inline-flex flex-wrap items-center gap-1">
                  {r.customerId ? (
                    <span className="font-mono font-semibold text-slate-600">ID {r.customerId}</span>
                  ) : null}
                  {r.customerId ? <span>·</span> : null}
                  <span>{r.date || '—'}</span>
                  <span>·</span>
                  {r.cancelled ? <CancelledReceiptMark /> : null}
                  <span className={r.cancelled ? 'line-through' : ''}>Bill #{r.billNumber || '—'}</span>
                </span>
              }
              fields={[
                { label: 'Amount', value: `−${money(r.amount)}` },
                { label: 'Recorded by', value: r.recordedBy || '—' },
              ]}
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => setDetailPayment(r)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    Details
                  </button>
                  {r.customerId ? (
                    <Link
                      to={`/dashboard/customers/${encodeURIComponent(r.customerId)}`}
                      className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100"
                    >
                      Customer
                    </Link>
                  ) : null}
                </>
              }
            />
          ))
        )}
      </div>
      <div className={`hidden sm:block ${scrollTableWrap}`}>
        <table className="w-full min-w-[720px] data-table border-separate border-spacing-0 text-left text-sm">
          <thead className={stickyThead}>
            <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className={`whitespace-nowrap px-4 py-3 ${stickyFirstTh}`}>Date</th>
              <th className="whitespace-nowrap px-4 py-3 font-mono">Bill #</th>
              <th className="px-4 py-3">Customer</th>
              <th className="whitespace-nowrap px-4 py-3 text-right">Amount</th>
              <th className="whitespace-nowrap px-4 py-3">Recorded by</th>
              <th className="whitespace-nowrap px-4 py-3 text-center"> </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-800">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  No payments yet. Record one to update customer balances.
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  No payments match your search or filters.
                </td>
              </tr>
            ) : (
              pagedRows.map((r) => (
                <tr
                  key={r.id}
                  {...detailRowAttrs(
                    () => setDetailPayment(r),
                    `hover:bg-slate-50/80 ${r.cancelled ? 'opacity-75' : ''}`,
                  )}
                  aria-label={`Payment ${r.cancelled ? 'cancelled ' : ''}${r.billNumber || r.id || ''}`}
                >
                  <td className={`whitespace-nowrap px-4 py-3 tabular-nums ${stickyFirstTd}`}>{r.date}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-semibold tabular-nums text-slate-800">
                    <span className="inline-flex items-center gap-1.5">
                      {r.cancelled ? <CancelledReceiptMark /> : null}
                      <span className={r.cancelled ? 'text-slate-400 line-through' : ''}>{r.billNumber || '—'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{r.customerName || '—'}</p>
                    {r.customerId ? (
                      <p className="mt-0.5 font-mono text-xs text-slate-500">ID {r.customerId}</p>
                    ) : null}
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums ${
                      r.cancelled ? 'text-slate-400 line-through' : 'text-emerald-700'
                    }`}
                  >
                    −{money(r.amount)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{r.recordedBy || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <Link
                      to={`/dashboard/customers/${encodeURIComponent(r.customerId)}`}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Customer
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 ? (
        <TablePaginationBar
          page={pagination.page}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalCount={filteredRows.length}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
        />
      ) : null}
      </div>

      <RecordPaymentModal
        open={modalOpen}
        onClose={closeModal}
        onSaved={async (row) => {
          await load();
          if (!editPayment) requestAutoPrint('cashCollection', row);
        }}
        prefillCustomerId={modalCustomerId}
        editPayment={editPayment}
      />

      <CollectorSeparateBillSettlementModal
        open={separateBillModalOpen}
        onClose={closeSeparateBillModal}
        onSaved={async (row) => {
          await load();
          requestAutoPrint('cashCollection', row);
        }}
        prefillCustomerId={modalCustomerId}
      />

      <RowDetailModal
        open={!!detailPayment}
        row={detailPayment}
        variant="payment"
        onClose={() => setDetailPayment(null)}
        actions={
          isCollector() || isManagerOrAdmin() ? (
            <div className="mt-4 flex flex-col gap-2">
              {detailPayment?.cancelled ? (
                <p className="rounded-xl bg-rose-50 px-4 py-2.5 text-center text-xs font-medium text-rose-800 ring-1 ring-rose-100">
                  This receipt is cancelled
                  {detailPayment.cancelledBy ? ` by ${detailPayment.cancelledBy}` : ''}.
                </p>
              ) : collectorMayPrintCashCollection(detailPayment) ? (
                <button
                  type="button"
                  onClick={() => {
                    if (!collectorMayPrintCashCollection(detailPayment)) return;
                    requestPrint('cashCollection', detailPayment);
                  }}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm font-semibold text-sky-800 ring-1 ring-sky-100 hover:bg-sky-100"
                >
                  <PrinterGlyph className="h-4 w-4" />
                  Print bill
                </button>
              ) : (
                <p className="rounded-xl bg-slate-50 px-4 py-2.5 text-center text-xs text-slate-500 ring-1 ring-slate-100">
                  {COLLECTOR_CASH_PRINT_TODAY_ONLY}
                </p>
              )}
              {isAdmin() && detailPayment && !detailPayment.cancelled ? (
                <PaymentReceiptPdfButton payment={detailPayment} />
              ) : null}
              {canEditDetails() && detailPayment && !detailPayment.cancelled ? (
                <button
                  type="button"
                  onClick={() => openPaymentEdit(detailPayment)}
                  className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-semibold text-indigo-800 ring-1 ring-indigo-100 hover:bg-indigo-100"
                >
                  Edit payment
                </button>
              ) : null}
              {isAdmin() && detailPayment && !detailPayment.cancelled ? (
                <button
                  type="button"
                  onClick={() => openCancelConfirm(detailPayment)}
                  className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-800 ring-1 ring-rose-100 hover:bg-rose-100"
                >
                  Cancel receipt
                </button>
              ) : null}
            </div>
          ) : null
        }
      />

      {cancelTarget ? (
        <div className="fixed inset-0 z-[110] flex items-end justify-center p-4 sm:items-center">
          <ModalBackdrop onClose={closeCancelConfirm} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-cancel-title"
            className={`${modalPanelClass} w-full max-w-md`}
          >
            <h2 id="payment-cancel-title" className="text-lg font-bold text-slate-900">
              Cancel payment receipt?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              This will cancel receipt{' '}
              <span className="font-semibold text-slate-900">#{cancelTarget.billNumber || '—'}</span>
              {cancelTarget.customerName ? (
                <>
                  {' '}
                  for <span className="font-semibold text-slate-900">{cancelTarget.customerName}</span>
                </>
              ) : null}
              . The receipt stays on this page with a Cancelled mark in front of the bill number, and it no longer
              reduces the customer balance. This cannot be undone.
            </p>
            {cancelError ? (
              <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-100" role="alert">
                {cancelError}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeCancelConfirm}
                disabled={cancelBusy}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Keep receipt
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={cancelBusy}
                className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 disabled:opacity-60"
              >
                {cancelBusy ? 'Cancelling…' : 'Cancel receipt'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CancelledReceiptMark() {
  return (
    <span className="inline-flex shrink-0 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700 ring-1 ring-rose-100">
      Cancelled
    </span>
  );
}

function PrinterGlyph({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 3.75A.75.75 0 017.75 3h8.5a.75.75 0 01.75.75V7h.75A2.25 2.25 0 0120 9.25v6.5A2.25 2.25 0 0117.75 18H17v2.25a.75.75 0 01-.75.75h-8.5a.75.75 0 01-.75-.75V18H6.25A2.25 2.25 0 014 15.75v-6.5A2.25 2.25 0 016.25 7H7V3.75zM8.5 4.5v2.5h7V4.5h-7zM6.25 8.5a.75.75 0 00-.75.75v6.5c0 .414.336.75.75.75H7v-1.25a.75.75 0 01.75-.75h8.5a.75.75 0 01.75.75V16.5h.75a.75.75 0 00.75-.75v-6.5a.75.75 0 00-.75-.75H6.25zM9 16.5v3h6v-3H9zM8 11.25a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75z" />
    </svg>
  );
}
